import { PropertyKind } from "shared/types";
import { ApartmentsComVariant, Unit } from "types/landlord/address";

export interface SyndicationState {
  zillow: boolean;
  zillowSpotlight: boolean;
  apartmentsCom: boolean;
  apartmentsComVariant: ApartmentsComVariant | null;
  zumper: boolean;
  dwellsy: boolean;
  affordableHousingCom: boolean;
}

interface MagicLinkSnapshot {
  linkIsActive: boolean;
  unitListed: boolean;
  syndication: SyndicationState;
}

interface MagicLinkPayload {
  active?: boolean;
  listed?: boolean;
  feed_zillow_enabled?: boolean;
  feed_zillow_spotlight_enabled?: boolean;
  feed_apartments_com_enabled?: boolean;
  feed_apartments_com_variant?: ApartmentsComVariant | null;
  feed_zumper_enabled?: boolean;
  feed_dwellsy_enabled?: boolean;
  feed_affordable_housing_com_enabled?: boolean;
}

const PROPERTY_KIND_TO_APARTMENTS_VARIANT: Record<
  string,
  ApartmentsComVariant
> = {
  [PropertyKind.SingleFamily]: "single_family",
  [PropertyKind.MultiFamily]: "multi_family",
  default: "single_family"
};

export const apartmentsVariantForKind = (
  propertyKind: PropertyKind | null
): ApartmentsComVariant =>
  PROPERTY_KIND_TO_APARTMENTS_VARIANT[propertyKind ?? "default"];

export const readSyndicationState = (
  unit: Unit,
  defaultVariant: ApartmentsComVariant
): SyndicationState => ({
  zillow: Boolean(unit.feed_zillow_enabled),
  zillowSpotlight: Boolean(unit.feed_zillow_spotlight_enabled),
  apartmentsCom: Boolean(unit.feed_apartments_com_enabled),
  apartmentsComVariant:
    unit.feed_apartments_com_variant ??
    (unit.feed_apartments_com_enabled ? defaultVariant : null),
  zumper: Boolean(unit.feed_zumper_enabled),
  dwellsy: Boolean(unit.feed_dwellsy_enabled),
  affordableHousingCom: Boolean(unit.feed_affordable_housing_com_enabled)
});

export const withZillowToggled = (
  state: SyndicationState
): SyndicationState => {
  const nextZillow = !state.zillow;
  return {
    ...state,
    zillow: nextZillow,
    zillowSpotlight: nextZillow ? state.zillowSpotlight : false
  };
};

export const withZumperToggled = (
  state: SyndicationState
): SyndicationState => ({
  ...state,
  zumper: !state.zumper
});

export const withDwellsyToggled = (
  state: SyndicationState
): SyndicationState => ({ ...state, dwellsy: !state.dwellsy });

export const withAffordableHousingComToggled = (
  state: SyndicationState
): SyndicationState => ({
  ...state,
  affordableHousingCom: !state.affordableHousingCom
});

export const withApartmentsComToggled = (
  state: SyndicationState,
  defaultVariant: ApartmentsComVariant
): SyndicationState => {
  if (state.apartmentsCom)
    return { ...state, apartmentsCom: false, apartmentsComVariant: null };

  return {
    ...state,
    apartmentsCom: true,
    apartmentsComVariant: state.apartmentsComVariant ?? defaultVariant
  };
};

export const withApartmentsVariant = (
  state: SyndicationState,
  variant: ApartmentsComVariant
): SyndicationState => ({ ...state, apartmentsComVariant: variant });

export const diffMagicLinkPayload = (
  previous: MagicLinkSnapshot,
  next: MagicLinkSnapshot
): MagicLinkPayload => {
  const payload: MagicLinkPayload = {};

  if (previous.linkIsActive !== next.linkIsActive) {
    payload.active = next.linkIsActive;
    payload.listed = next.unitListed;
  }
  if (previous.unitListed !== next.unitListed) payload.listed = next.unitListed;
  if (previous.syndication.zillow !== next.syndication.zillow)
    payload.feed_zillow_enabled = next.syndication.zillow;

  const nextSpotlight =
    next.syndication.zillow && next.syndication.zillowSpotlight;
  const previousSpotlight =
    previous.syndication.zillow && previous.syndication.zillowSpotlight;
  if (previousSpotlight !== nextSpotlight)
    payload.feed_zillow_spotlight_enabled = nextSpotlight;

  if (previous.syndication.apartmentsCom !== next.syndication.apartmentsCom)
    payload.feed_apartments_com_enabled = next.syndication.apartmentsCom;

  const nextVariant = next.syndication.apartmentsCom
    ? next.syndication.apartmentsComVariant
    : null;
  const previousVariant = previous.syndication.apartmentsCom
    ? previous.syndication.apartmentsComVariant
    : null;
  if (previousVariant !== nextVariant)
    payload.feed_apartments_com_variant = nextVariant;

  if (previous.syndication.zumper !== next.syndication.zumper)
    payload.feed_zumper_enabled = next.syndication.zumper;

  if (previous.syndication.dwellsy !== next.syndication.dwellsy)
    payload.feed_dwellsy_enabled = next.syndication.dwellsy;

  if (
    previous.syndication.affordableHousingCom !==
    next.syndication.affordableHousingCom
  )
    payload.feed_affordable_housing_com_enabled =
      next.syndication.affordableHousingCom;

  return payload;
};
